import React, { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Dialog xác nhận dùng chung — thay thế window.confirm / alert.
 *
 * Props:
 *   open, title, message, confirmText, cancelText, danger, loading,
 *   onConfirm, onCancel
 */
function ConfirmDialog({
    open,
    title = "Xác nhận",
    message,
    confirmText = "Đồng ý",
    cancelText = "Hủy",
    danger = false,
    loading = false,
    onConfirm,
    onCancel,
}) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape" && !loading) onCancel?.();
            if (e.key === "Enter" && !loading) onConfirm?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, loading, onCancel, onConfirm]);

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4"
            onClick={() => !loading && onCancel?.()}
            style={{ animation: "fadeIn 0.12s ease-out" }}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                style={{ animation: "pimSlideUp 0.15s ease-out" }}
            >
                <div className="p-5 flex items-start gap-3">
                    {danger && (
                        <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.8}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                                />
                            </svg>
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900">
                            {title}
                        </h3>
                        {message && (
                            <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                                {message}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
                    <button
                        type="button"
                        disabled={loading}
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        disabled={loading}
                        onClick={onConfirm}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${danger
                            ? "bg-red-600 hover:bg-red-700"
                            : "bg-gray-900 hover:bg-gray-800"
                            }`}
                    >
                        {loading ? "Đang xử lý…" : confirmText}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default ConfirmDialog;
